import React from "react";
import "./Features.css";
import { NavLink } from "react-router-dom";

const FeaturesPage = () => (
  <div className="features-page">
    <header className="page-hero">
      <h1>Feature Overview</h1>
      <p>Explore our smart locker system and its detailed features.</p>
    </header>

    <LockerSection
      title="Static Lockers"
      intro="In the Qeeper system, static locker assignment provides a fixed, long-term allocation of lockers, ideal for situations where users use the same locker for long periods and on a regular basis. This system is particularly suitable for permanent employees or students who need daily or long-term access to a specific locker. With static assignment, the locker administrator assigns a specific locker to a user, and it remains available to the user until access rights are explicitly revoked."
      functionalities={[
        "Fixed Assignment: Each user always knows which locker is assigned to them.",
        "Authentication methods: Access via RFID card, PIN code, QR and Barcode, or smartphone by web application and the MyLocker mobile app.",
        "Simplified Administration: Fewer changes and reconfigurations are required, saving time.",
        "Security Advantages: Reduces the risk of users accessing others' lockers.",
        "Access Efficiency: Users can go directly to their designated locker, saving time in busy environments.",
      ]}
      benefits={[
        "Consistency for the User: Users experience greater comfort by always using the same locker, especially when storing items regularly.",
        "Lower Management Costs: Less need for dynamic allocation management and monitoring.",
        "Reduced Conflicts: Prevents conflicts that can arise when multiple people try to access the same locker.",
        "Enhanced Control: Static assignment systems make it easier to oversee who uses which locker.",
      ]}
    />

    <LockerSection
      title="Dynamic Lockers"
      grey
      intro="In the Qeeper system, dynamic locker assignment offers flexible, on-demand access to lockers. This setup is perfect for situations where users only need temporary use of a locker. Lockers are claimed and released by users themselves via a smartphone, RFID card, PIN or QR code. Dynamic assignment ensures that lockers are only in use when needed, maximizing efficiency and minimizing unused space. This system is ideal for shared environments such as offices, schools, fitness centres, or events, where locker demand varies throughout the day."
      functionalities={[
        "Self-Service Allocation: Users can independently claim and release lockers without administrative intervention.",
        "Time-Based Control: Locker usage duration or end time can be configured. Lockers can be auto-released or blocked after expiry.",
        "Access Flexibility: Claim lockers using RFID card, PIN code, QR and Barcode, or smartphone by web application and the MyLocker mobile app.",
        "Guest Access Support: Temporary use is available for guests with options like phone-based payment, guest PIN codes, or personal RFID cards.",
        "Smart Notifications: Admins receive alerts for expired locker sessions and other relevant events.",
      ]}
      benefits={[
        "Optimised Use of Space: Lockers are only in use when actually needed, reducing the total number required.",
        "Lower Operational Costs: Fewer lockers mean lower costs for purchase, installation and maintenance.",
        "Ideal for Shared Spaces: Perfect for dynamic environments like coworking spaces, universities or events.",
        "No Waiting Lines: Users don't need to queue or wait for locker assignment.",
        "Easy for Guests and One-Time Users: No account needed for temporary access—just scan and go.",
      ]}
    />

    <LockerSection
      title="Guest Lockers"
      intro="Qeeper's Guest Lockers provide secure and hassle-free temporary storage for visitors, event attendees, or anyone without a pre-registered account. These lockers are ideal for public spaces, cultural venues, educational institutions, and event locations where short-term access to lockers is required. Guests can claim, use, and release lockers independently using easy access methods such as QR codes, PIN codes, or RFID cards—without the need for administrator involvement."
      functionalities={[
        "Self-Service Access: Visitors can claim lockers on the spot using smartphone-based access or on-site terminals.",
        "Temporary Credentials: Access can be granted via a temporary guest access card, a self-chosen PIN, or even existing cards like public transport or museum passes.",
        "Payment Integration: Optional payment system allows guests to rent lockers for a fee via mobile app or web.",
        "Timed Use & Auto Release: Predefined usage time with automatic release options after expiry.",
      ]}
      benefits={[
        "No Pre-Registration Required: Perfect for one-time or irregular visitors who don't need a permanent locker.",
        "Fast & Easy to Use: Users can access lockers within seconds, enhancing flow in busy environments.",
        "Reduces Staff Workload: No need for reception staff to manage locker handouts or returns.",
        "Ideal for Public Environments: Supports guest flows at events, exhibitions, libraries, or hospitality settings.",
      ]}
    />

    <LockerSection
      title="Team Lockers"
      grey
      intro="Team Lockers in the Qeeper system offer shared access to lockers by multiple users or user groups. This configuration allows lockers to be assigned to teams or departments, making it easy to manage access to shared resources. Whether for a group of technicians needing access to shared tools or a team of employees storing equipment, Team Lockers promote collaboration and streamline locker management."
      functionalities={[
        "Multi-User Assignment: Assign one locker to multiple individuals or an entire group.",
        "Role-Based Access: Set locker restrictions and grant exclusive access privileges based on user roles or team memberships.",
        "Centralized Configuration: Manage team assignments easily via the admin panel or automated integrations.",
        "Flexible Group Management: Users can be dynamically added or removed from locker groups as team structures change.",
      ]}
      benefits={[
        "Improved Collaboration: Shared locker access supports teamwork and efficient resource sharing.",
        "Reduced Locker Footprint: Fewer lockers are needed when multiple people share access.",
        "Easier Administration: Simplified management of access rights through group-level settings.",
        "Access Transparency: Activity tracking ensures that shared locker use is still auditable.",
      ]}
    />

    <section className="page-cta">
      <h2>Discover More Features</h2>
      <p>Contact us today to see how Qeeper can fit your organization.</p>
      <NavLink to="/contact" className="page-cta__button">
        Contact us
      </NavLink>
    </section>
  </div>
);

const LockerSection = ({ title, intro, functionalities, benefits, grey }) => (
  <section className={`feature-section${grey ? " feature-section--grey" : ""}`}>
    <div className="feature-section__inner">
      <h2>{title}</h2>
      <p className="feature-section__intro">{intro}</p>

      <div className="feature-section__details">
        <div>
          <h3>Functionalities</h3>
          <ul>
            {functionalities.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3>Benefits</h3>
          <ul>
            {benefits.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export default FeaturesPage;
